import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { setAuthHeader, clearAuthHeader } from '../../services/api';
import { clearAllUserData } from '../../utils/logout';

console.log('🚀 AUTH SLICE LOADED - CODE IS UPDATED!');
console.log('🔧 setAuthHeader function:', typeof setAuthHeader);
console.log('🔧 clearAuthHeader function:', typeof clearAuthHeader);

// Helper function to safely parse JSON from localStorage
const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    localStorage.removeItem('user'); // Clear corrupted data
    return null;
  }
};

// Helper function to check if token exists and is valid format
const getStoredToken = () => {
  const token = localStorage.getItem('token');
  return token && token.length > 0 ? token : null;
};

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      // Response is already unwrapped by interceptor, so response = { success, message, data, twofa_type }
      if (response.success) {
        const { user, token, refreshToken, redirect_url, questions, phone } = response.data;
        const { twofa_type } = response;
        
        console.log('📋 Login response:', { user, token, refreshToken, redirect_url, questions, twofa_type, phone });
        console.log('📋 Questions:', questions);
        if (questions && questions.length > 0) {
          console.log('📋 First question:', questions[0]);
          console.log('📋 First question ID:', questions[0].id);
        }
        
        // If 2FA is required, return 2FA data instead of logging in
        if (twofa_type === 'security_que' && questions) {
          return {
            user,
            token: null,
            refreshToken: null,
            redirect_url: null,
            twofa_type: 'security_que',
            questions: questions,
            email: email,
            password: password,
            requires2FA: true
          };
        }

        // If OTP 2FA is required (email or sms)
        if ((twofa_type === 'email' || twofa_type === 'sms') && user) {
          return {
            user,
            token: null,
            refreshToken: null,
            redirect_url: null,
            twofa_type: twofa_type,
            phone: phone,
            email: email,
            password: password,
            requires2FA: true
          };
        }
        
        // Store token and user data only if no 2FA required
        if (token && refreshToken) {
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('user', JSON.stringify(user));
          // Set auth header for all future requests
          setAuthHeader(token);
          console.log('✅ Login successful - Token set in axios defaults');
        }
        
        return { user, token, refreshToken, redirect_url, requires2FA: false };
      } else {
        return rejectWithValue(response.message || 'Login failed');
      }
    } catch (error) {
      // Handle both API error responses and network errors
      const errorMessage = error.message || error.response?.data?.message || 'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);

      if (response.success) {
        // Registration successful - user needs to verify email
        // Don't store token/user data yet, wait for email verification
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout');

      // Clear all user data from storage
      clearAllUserData();

      // Clear auth header
      clearAuthHeader();

      return true;
    } catch (error) {
      // Even if API call fails, clear all user data
      clearAllUserData();

      // Clear auth header
      clearAuthHeader();

      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getUserProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/profile');

      if (response.success) {
        return response.data.user;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile', profileData);

      if (response.success) {
        // Update user data in localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      // Handle validation errors
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfileImage = createAsyncThunk(
  'auth/updateProfileImage',
  async (imageData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/profile/image', { crop_image_text: imageData });

      if (response.success) {
        // Update user data in localStorage with new image
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { 
          ...storedUser, 
          image: response.data.user.image,
          image_url: response.data.user.image_url
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        return {
          user: response.data.user,
          image_url: response.data.user.image_url
        };
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ current_password, password, new_password }, { rejectWithValue }) => {
    try {
      const finalNewPassword = new_password || password;
      const response = await api.post('/auth/change-password', {
        current_password,
        password: finalNewPassword,
      });

      if (response.success) {
        return response.message;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/verify-email/${token}?email=${encodeURIComponent(email)}`);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const setupPassword = createAsyncThunk(
  'auth/setupPassword',
  async ({ token, email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/setup-password', {
        token,
        email,
        password,
        password_confirmation
      });

      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const validateResetToken = createAsyncThunk(
  'auth/validateResetToken',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/validate-reset-token/${token}?email=${encodeURIComponent(email)}`);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password,
        password_confirmation
      });

      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        return rejectWithValue(error.data.errors.join(', '));
      }
      return rejectWithValue(error.message);
    }
  }
);

// 2FA Thunks
export const enable2FA = createAsyncThunk(
  'auth/enable2FA',
  async ({ method, security_questions }, { rejectWithValue }) => {
    try {
      const payload = { method };
      if (security_questions) {
        payload.security_questions = security_questions;
      }
      
      const response = await api.post('/2fa/enable', payload);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const disable2FA = createAsyncThunk(
  'auth/disable2FA',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/2fa/disable');

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const request2FAOTP = createAsyncThunk(
  'auth/request2FAOTP',
  async ({ email, method = 'email' }, { rejectWithValue }) => {
    try {
      const endpoint = method === 'sms' ? '/2fa/request-sms-otp' : '/2fa/request-email-otp';
      const payload = method === 'sms' ? { phone_number: email } : { email };
      
      const response = await api.post(endpoint, payload);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verify2FAOTP = createAsyncThunk(
  'auth/verify2FAOTP',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await api.post('/2fa/verify-otp', { email, otp });

      if (response.success) {
        // Store tokens from 2FA verification
        const { access_token, refresh_token } = response.data;
        if (access_token && refresh_token) {
          localStorage.setItem('token', access_token);
          localStorage.setItem('refreshToken', refresh_token);
          setAuthHeader(access_token);
        }
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const resend2FAOTP = createAsyncThunk(
  'auth/resend2FAOTP',
  async ({ email, method = 'email' }, { rejectWithValue }) => {
    try {
      const response = await api.post('/2fa/resend-otp', { email, method });

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verify2FASecurityQuestion = createAsyncThunk(
  'auth/verify2FASecurityQuestion',
  async ({ email, qId, answer }, { rejectWithValue }) => {
    try {
      const response = await api.post('/2fa/verify-security-questions', { email, qId, answer });

      if (response.success) {
        // Store tokens from 2FA verification
        const { token, refreshToken } = response.data;
        if (token && refreshToken) {
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          setAuthHeader(token);
        }
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const get2FASecurityQuestions = createAsyncThunk(
  'auth/get2FASecurityQuestions',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/2fa/security-questions?email=${encodeURIComponent(email)}`);

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  user: getStoredUser(),
  token: getStoredToken(),
  refreshToken: localStorage.getItem('refreshToken') || null,
  isAuthenticated: !!(getStoredToken() && getStoredUser()),
  loading: false,
  error: null,
  profileLoading: false,
  passwordChangeLoading: false,
  twoFALoading: false,
  redirectUrl: null, // Add redirect URL for role-based navigation
  isInitialized: true, // Track if auth state has been initialized
  resetData: null, // Store reset password data (token, email, resetUrl)
  twofa_type: null, // 2FA type if required
  twofa_questions: null, // Security questions for 2FA
  twofa_email: null, // Email for 2FA verification
  twofa_password: null, // Password for 2FA verification
  twofa_phone: null, // Phone number for SMS 2FA
  isInactive: false, // Track if account is inactive
};

// Debug: Log initial auth state
// console.log('🔐 Auth Initial State:', {
//   hasUser: !!initialState.user,
//   hasToken: !!initialState.token,
//   isAuthenticated: initialState.isAuthenticated,
//   userEmail: initialState.user?.email,
//   userType: initialState.user?.user_type,
//   isInitialized: initialState.isInitialized
// });

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.redirectUrl = null;
      state.resetData = null;
      state.isInitialized = true;
      state.isInactive = false;

      // Clear all user data from storage
      clearAllUserData();

      // Clear auth header
      clearAuthHeader();
    },
    setCredentials: (state, action) => {
      const { user, token, refreshToken } = action.payload;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.isInitialized = true;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      // Set auth header for all future requests
      setAuthHeader(token);
    },
    clearRedirectUrl: (state) => {
      state.redirectUrl = null;
    },
    clear2FAState: (state) => {
      state.twofa_type = null;
      state.twofa_questions = null;
      state.twofa_email = null;
      state.twofa_password = null;
      state.twofa_phone = null;
    },
    // Helper function to get redirect URL based on user type
    getRedirectUrlForUserType: (state) => {
      if (!state.user?.user_type) return '/';

      switch (state.user.user_type) {
        case 'Admin':
        case 'SubAdmin':
          return '/admin'; // Admin panel on same app
        case 'Airliner':
          return '/airliner/profile'; // Internal route within React app
        case 'JetSetter':
          return '/jetsetter/profile/user-details';
        default:
          return '/'; // Default to search page
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.redirectUrl = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        
        // Check if 2FA is required
        if (action.payload.requires2FA) {
          state.twofa_type = action.payload.twofa_type;
          state.twofa_questions = action.payload.questions;
          state.twofa_email = action.payload.email;
          state.twofa_password = action.payload.password;
          state.twofa_phone = action.payload.phone;
          state.error = null;
          state.isInitialized = true;
        } else {
          // Normal login flow
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.error = null;
          state.redirectUrl = action.payload.redirect_url;
          state.isInitialized = true;
          // Clear 2FA data
          state.twofa_type = null;
          state.twofa_questions = null;
          state.twofa_email = null;
          state.twofa_password = null;
          state.twofa_phone = null;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.redirectUrl = null;
        state.isInitialized = true;
        // Store inactive status if present
        if (action.error?.isInactive) {
          state.isInactive = true;
        }
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Don't set user as authenticated yet - they need to verify email
        // Just show success message
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        // Reset all state to initial values
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.redirectUrl = null;
        state.profileLoading = false;
        state.passwordChangeLoading = false;
        state.isInitialized = true;
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
        state.twofa_phone = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout fails, clear the state
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.redirectUrl = null;
        state.profileLoading = false;
        state.passwordChangeLoading = false;
        state.isInitialized = true;
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
        state.twofa_phone = null;
      })

      // Get Profile
      .addCase(getUserProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload;
      })

      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload;
      })

      // Update Profile Image
      .addCase(updateUserProfileImage.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(updateUserProfileImage.fulfilled, (state, action) => {
        state.profileLoading = false;
        // Update user image and image_url in state
        if (state.user) {
          state.user = { 
            ...state.user, 
            image: action.payload.user.image,
            image_url: action.payload.image_url
          };
          // Also update localStorage
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(updateUserProfileImage.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload;
      })

      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.passwordChangeLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordChangeLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordChangeLoading = false;
        state.error = action.payload;
      })

      // Email Verification
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Email verified successfully, but user still needs to set password
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Password Setup
      .addCase(setupPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setupPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Password setup complete - user can now login
      })
      .addCase(setupPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Store the reset data for potential use
        state.resetData = action.payload;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Validate Reset Token
      .addCase(validateResetToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateResetToken.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(validateResetToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.resetData = null; // Clear reset data after successful password reset
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Enable 2FA
      .addCase(enable2FA.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(enable2FA.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        // Update user 2FA status
        if (state.user) {
          state.user.Twofa = action.payload.method;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Disable 2FA
      .addCase(disable2FA.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(disable2FA.fulfilled, (state) => {
        state.twoFALoading = false;
        state.error = null;
        // Clear user 2FA status
        if (state.user) {
          state.user.Twofa = null;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Request 2FA OTP
      .addCase(request2FAOTP.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(request2FAOTP.fulfilled, (state) => {
        state.twoFALoading = false;
        state.error = null;
      })
      .addCase(request2FAOTP.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Verify 2FA OTP
      .addCase(verify2FAOTP.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(verify2FAOTP.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        // Complete login after 2FA verification
        state.user = action.payload.user || action.payload;
        state.token = action.payload.access_token || action.payload.token;
        state.refreshToken = action.payload.refresh_token || action.payload.refreshToken;
        state.isAuthenticated = true;
        state.redirectUrl = action.payload.redirect_url;
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
      })
      .addCase(verify2FAOTP.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Resend 2FA OTP
      .addCase(resend2FAOTP.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(resend2FAOTP.fulfilled, (state) => {
        state.twoFALoading = false;
        state.error = null;
      })
      .addCase(resend2FAOTP.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Verify 2FA Security Question
      .addCase(verify2FASecurityQuestion.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(verify2FASecurityQuestion.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        // Complete login after 2FA verification
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.redirectUrl = action.payload.redirect_url;
        // Clear 2FA data
        state.twofa_type = null;
        state.twofa_questions = null;
        state.twofa_email = null;
        state.twofa_password = null;
      })
      .addCase(verify2FASecurityQuestion.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      })

      // Get 2FA Security Questions
      .addCase(get2FASecurityQuestions.pending, (state) => {
        state.twoFALoading = true;
        state.error = null;
      })
      .addCase(get2FASecurityQuestions.fulfilled, (state, action) => {
        state.twoFALoading = false;
        state.error = null;
        state.twofa_questions = action.payload;
      })
      .addCase(get2FASecurityQuestions.rejected, (state, action) => {
        state.twoFALoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearAuth, setCredentials, clearRedirectUrl, clear2FAState } = authSlice.actions;
export default authSlice.reducer;